import React, { useCallback, useState, useRef, useMemo, memo } from 'react'

import { useDropdownPosition } from '@/lib/hooks/useDropdownPosition'
import { cn } from '@/lib/utils'
import Icon, { IconTypes } from '@/components/Icon'
import useClickOutside from '@/lib/hooks/useClickOutside'
import Button from '@/components/livecontact/Button'
import FieldError from '@/components/livecontact/FieldError'
import LcIcon from '@/components/livecontact/icons'
import {
  CHIP_CLASS,
  CHIP_REMOVE_CLASS,
  INPUT_CLASS,
  INPUT_ERROR_CLASS,
  LABEL_CLASS,
  NOTE_CLASS,
  OPTION_CLASS,
  POP_CLASS,
} from '@/components/livecontact/fieldStyles'

type Props = {
  className?: string
  classInput?: string
  classLabel?: string
  label?: string
  note?: string
  value?: string[]
  onChange?: (tags: string[]) => void
  onRemoveTag?: (tag: string) => void
  onAddTag?: (tag: string) => void
  placeholder?: string
  disabled?: boolean
  icon?: IconTypes
  error?: string
  classInnerTagList?: string
  tagList?: string[]
}

/** Removable tag chip (also used standalone). */
export const TagItem = memo(
  ({
    tag,
    onRemove,
    disabled,
  }: {
    tag: string
    onRemove: (tag: string) => void
    disabled?: boolean
  }) => {
    const handleRemove = useCallback(() => onRemove(tag), [tag, onRemove])

    return (
      <span className={cn(CHIP_CLASS, 'animate-lc-pop')}>
        <LcIcon name="tag" size={12} className="text-ink-4" />
        {tag}
        <button
          type="button"
          onClick={handleRemove}
          aria-label={`Remove ${tag}`}
          className={CHIP_REMOVE_CLASS}
          disabled={disabled}
        >
          <LcIcon name="close" size={12} />
        </button>
      </span>
    )
  }
)
TagItem.displayName = 'TagItem'

const DropdownTagItem = memo(function DropdownTagItem({
  tag,
  onSelect,
}: {
  tag: string
  onSelect: (tag: string) => void
}) {
  const handleClick = useCallback(() => onSelect(tag), [tag, onSelect])

  return (
    <button type="button" onClick={handleClick} className={cn(OPTION_CLASS, 'py-1.5')}>
      <LcIcon name="tag" className="text-ink-4" />
      {tag}
    </button>
  )
})

/** Tag input: text field + Add, suggestions from `tagList`, chips below (or above via classInnerTagList). */
const TagField = memo(function TagField({
  className = '',
  classInput = '',
  classLabel = '',
  classInnerTagList,
  label,
  note,
  value = [],
  onChange,
  onRemoveTag,
  onAddTag,
  placeholder = 'Add a tag…',
  disabled = false,
  icon,
  error,
  tagList = [],
}: Props) {
  const [tagInput, setTagInput] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const dropdownPosition = useDropdownPosition({
    isOpen: isDropdownOpen,
    triggerRef: inputRef,
  })

  const availableTags = useMemo(
    () => tagList.filter((tag) => !value.includes(tag)),
    [tagList, value]
  )

  const addTag = useCallback(() => {
    const newTag = tagInput.trim()
    if (!newTag || value.includes(newTag)) return
    const updated = [...value, newTag]
    onChange?.(updated)
    onAddTag?.(newTag)
    setTagInput('')
    setIsDropdownOpen(false)
  }, [tagInput, value, onChange, onAddTag])

  const selectTag = useCallback(
    (tag: string) => {
      if (value.includes(tag)) return
      const updated = [...value, tag]
      onChange?.(updated)
      onAddTag?.(tag)
      setIsDropdownOpen(false)
      setTagInput('')
    },
    [value, onChange, onAddTag]
  )

  const removeTag = useCallback(
    (tag: string) => {
      const updated = value.filter((t) => t !== tag)
      onChange?.(updated)
      onRemoveTag?.(tag)
    },
    [value, onChange, onRemoveTag]
  )

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        addTag()
      }
      if (e.key === 'Escape') setIsDropdownOpen(false)
    },
    [addTag]
  )

  const closeDropdown = useCallback(() => setIsDropdownOpen(false), [])
  useClickOutside(dropdownRef, closeDropdown, isDropdownOpen)

  const hasValue = value.length > 0
  const hasAvailableTags = availableTags.length > 0
  const canAddTag = !disabled && tagInput.trim().length > 0

  return (
    <div className={cn('flex flex-col', className)}>
      {label && <label className={cn(LABEL_CLASS, classLabel)}>{label}</label>}

      <div className="flex flex-col gap-3">
        <div className="relative flex items-center gap-2">
          {icon && <Icon name={icon} className="size-4 shrink-0 fill-ink-4" />}
          <div className="relative flex-1">
            <input
              ref={inputRef}
              className={cn(INPUT_CLASS, error && INPUT_ERROR_CLASS, classInput)}
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onFocus={() => hasAvailableTags && setIsDropdownOpen(true)}
              onKeyDown={handleInputKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              aria-invalid={!!error}
            />

            {isDropdownOpen && hasAvailableTags && (
              <div
                ref={dropdownRef}
                className={cn(
                  'absolute z-20 max-h-48 w-full overflow-y-auto animate-lc-pop lc-scrollbar-thin',
                  POP_CLASS,
                  dropdownPosition === 'bottom' ? 'top-full mt-1.5 origin-top' : 'bottom-full mb-1.5 origin-bottom'
                )}
              >
                {availableTags.map((tag) => (
                  <DropdownTagItem key={tag} tag={tag} onSelect={selectTag} />
                ))}
              </div>
            )}
          </div>
          <Button type="button" variant="secondary" onClick={addTag} disabled={!canAddTag}>
            Add
          </Button>
        </div>

        {hasValue && (
          <div className={cn('flex flex-wrap gap-2', classInnerTagList)}>
            {value.map((tag) => (
              <TagItem key={tag} tag={tag} onRemove={removeTag} disabled={disabled} />
            ))}
          </div>
        )}
      </div>

      {note && !error && <p className={NOTE_CLASS}>{note}</p>}
      <FieldError message={error} />
    </div>
  )
})

export default TagField
